import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";

import type { ProjectService, TProjectAttachment } from "@/services/project.service";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { patchScoped } from "@/queries/invalidation";
import {
  projectActivitiesKey,
  projectAttachmentsKey,
  projectAttachmentsQuery,
} from "@/queries/project-resources";
import { projectDetailKey } from "@/queries/projects";
import { queryCacheScope, type FlagSource } from "./query-cache-scope";

/**
 * A project's files, keyed by project id.
 *
 * Its own store rather than a slice of `ProjectStore`, matching the sibling
 * project-scoped stores (`ProjectActivityStore`, `ProjectCommentsStore`, and the
 * `project-*.store.ts` family): `ProjectStore` already carries list paging,
 * filtering, sorting, bulk operations and detail CRUD, and attachments share
 * none of that.
 *
 * The list lives in a store at all — rather than in the Files page — because the
 * count is read from outside that page: the project tab row renders it, and a
 * count you can only see while standing on the tab is showing it in the one
 * place it is redundant.
 *
 * Takes the service, not the root store. Every sibling holds a `RootStore` and
 * so sits in a `root.store → store → root.store` cycle; 67 of those are carried
 * in the dependency-cruiser baseline. This one needs one service off the root,
 * and the feature flags (`FlagSource`, a structural type) to know whether the
 * `query-cache` flag is on, so it does not have to join them.
 */
export class ProjectAttachmentsStore {
  attachmentsByProjectId = observable.map<string, TProjectAttachment[]>();
  loadingByProjectId = observable.map<string, boolean>();
  errorByProjectId = observable.map<string, boolean>();

  /**
   * Per-project write counter, bumped whenever an upload or a delete settles.
   * `fetch` samples it before its request and drops its own response if it
   * moved, so a slower list GET cannot land on top of a newer mutation and take
   * a just-uploaded file back off the screen (or put a deleted one back).
   *
   * The same shape as `ProjectStore.detailWriteSeq` and `loadTokenBySlug`, and
   * needed for the same reason: the Files page revalidates on mount while the
   * route loader is prefetching, so two GETs and a mutation can overlap.
   *
   * Deliberately not observable: it is bookkeeping about requests.
   */
  private writeSeq = new Map<string, number>();

  private service: ProjectService;
  private flags: FlagSource | null;

  constructor(service: ProjectService, flags: FlagSource | null = null) {
    this.service = service;
    this.flags = flags;
    makeAutoObservable(this, { service: false, writeSeq: false, flags: false } as never, {
      autoBind: true,
    });
  }

  /** The scope to cache under, or `null` when the `query-cache` flag is off. */
  private cacheScope(slug: string) {
    return this.flags ? queryCacheScope(this.flags, slug, "ProjectAttachmentsStore") : null;
  }

  getAttachments(projectId: string): TProjectAttachment[] {
    return this.attachmentsByProjectId.get(projectId) ?? [];
  }

  private markWrite(projectId: string): void {
    this.writeSeq.set(projectId, (this.writeSeq.get(projectId) ?? 0) + 1);
  }

  /** Load a project's files. Keeps whatever is already cached on screen while
   *  the request is in flight, so a revisit re-renders instead of flashing a
   *  spinner over a list it already has.
   *
   *  With the `query-cache` flag on, the read goes through the cached list (R5,
   *  `app/queries/project-resources.ts`), so the tab layout's loader, the Files
   *  page's mount and a re-entry within the freshness window share one request. */
  async fetch(slug: string, projectId: string): Promise<void> {
    const seqAtRequest = this.writeSeq.get(projectId) ?? 0;
    const scope = this.cacheScope(slug);
    this.loadingByProjectId.set(projectId, true);
    this.errorByProjectId.set(projectId, false);
    try {
      const attachments = scope
        ? await getQueryClient().fetchQuery(projectAttachmentsQuery(this.service, scope, projectId))
        : await this.service.listAttachments(slug, projectId);
      runInAction(() => {
        this.loadingByProjectId.set(projectId, false);
        // An upload or delete settled while this was away, so this payload
        // predates it. Drop it rather than undoing the newer change, and drop
        // the cached copy of it too, so the next read asks again.
        if ((this.writeSeq.get(projectId) ?? 0) !== seqAtRequest) {
          if (scope) {
            peekQueryClient()?.removeQueries({
              queryKey: projectAttachmentsKey(scope, projectId),
              exact: true,
            });
          }
          return;
        }
        this.attachmentsByProjectId.set(projectId, attachments);
      });
    } catch (error) {
      console.error("[ProjectAttachmentsStore] failed to load project files", {
        slug,
        projectId,
        error,
      });
      runInAction(() => {
        this.loadingByProjectId.set(projectId, false);
        this.errorByProjectId.set(projectId, true);
      });
    }
  }

  /** Upload one file and prepend it to the cached list. Throws on failure so
   *  the caller can tell the user which file it was. */
  async upload(
    slug: string,
    projectId: string,
    file: File,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const uploaded = await this.service.uploadAttachment(slug, projectId, file);
    runInAction(() => {
      this.markWrite(projectId);
      this.attachmentsByProjectId.set(projectId, [uploaded, ...this.getAttachments(projectId)]);
    });
    this.afterWrite(slug, projectId, (list) => [uploaded, ...list]);
    // Ids and the size only: a file name is free text a user chose.
    track("project_attachment_added", {
      project_id: projectId,
      attachment_id: uploaded.id,
      size: file.size,
      source,
    });
  }

  /** Remove one file. Throws on failure, leaving the cached list untouched —
   *  the row stays, which is the truth if the delete did not happen. */
  async remove(
    slug: string,
    projectId: string,
    attachmentId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.deleteAttachment(slug, projectId, attachmentId);
    runInAction(() => {
      this.markWrite(projectId);
      this.attachmentsByProjectId.set(
        projectId,
        this.getAttachments(projectId).filter((a) => a.id !== attachmentId),
      );
    });
    this.afterWrite(slug, projectId, (list) => list.filter((a) => a.id !== attachmentId));
    track("project_attachment_deleted", {
      project_id: projectId,
      attachment_id: attachmentId,
      source,
    });
  }

  /**
   * Keep the cache in step with a confirmed upload or delete (contract §3.1):
   * the cached list (R5) is patched like the map above; the activity feed (R3)
   * and the project detail (R1) are invalidated, since the server logged an
   * activity row and the detail carries the file count.
   */
  private afterWrite(
    slug: string,
    projectId: string,
    update: (list: TProjectAttachment[]) => TProjectAttachment[],
  ): void {
    const scope = this.cacheScope(slug);
    const client = peekQueryClient();
    if (!scope || !client) return;
    patchScoped(client, projectAttachmentsKey(scope, projectId), update);
    for (const queryKey of [
      projectActivitiesKey(scope, projectId),
      projectDetailKey(scope, projectId),
    ]) {
      client.invalidateQueries({ queryKey, exact: true }).catch((error: unknown) => {
        console.warn("[ProjectAttachmentsStore] invalidating after a file write failed", {
          slug,
          projectId,
          error,
        });
      });
    }
  }
}
